import React, { useMemo, useState } from "react";
import CustomModal from "@/shared/components/ui/Modal/CustomModal";
import ModalHeader from "@/shared/components/ui/Modal/ModalHeader";
import SecondaryButton from "@/shared/components/ui/Button/SecondaryButton";
import { PrimaryButton } from "@/shared/components/ui/Button/PrimaryButton";
import InlineAlert from "@/shared/components/ui/Alert/InlineAlert";
import { useUpdateCampaignStepAdvanceSettings } from "@/features/campaigns/hooks/useCampaign";
import { useCampaignId } from "@/features/campaigns/hooks/useCampaignId";
import MessageComposer from "../Messaging/MessageComposer";
import usePreviewLeads from "../Messaging/usePreviewLeads";
import {
  CONTENT_MODES,
  getComposerCopy,
  getStaticStepContent,
  isStaticContentStep,
  validateStaticStepContent,
} from "@/features/campaigns/utils/stepContentMode";

const ModalActions = ({ onCancel, onConfirm, confirmText, isSaving, disabled }) => (
  <div className="flex gap-2 justify-between w-full items-center">
    <SecondaryButton
      title="Cancel"
      textColor="dark"
      onClick={onCancel}
      className="shadow-none border-none w-fit text-gray-300 hover:text-gray-800"
    />
    <PrimaryButton
      startContent={false}
      children={isSaving ? "Saving" : confirmText}
      onClick={onConfirm}
      isLoading={isSaving}
      className="w-fit shadow-none"
      disabled={disabled}
    />
  </div>
);

/**
 * Write or edit the step's fixed message. Opening it from the AI toggle is the confirmation:
 * nothing changes until the message is saved.
 */
export const FixedMessageModal = ({ isOpen, onClose, step = {} }) => {
  const campaignId = useCampaignId();
  const channel = String(step?.channel || "").toLowerCase();
  const hasSubject = getComposerCopy(channel).hasSubject;
  const wasStatic = isStaticContentStep(step);
  const initial = getStaticStepContent(step);
  const [subject, setSubject] = useState(initial.subject);
  const [body, setBody] = useState(initial.content);
  const { mutate: saveStep, isPending } = useUpdateCampaignStepAdvanceSettings();
  const { leads, sender } = usePreviewLeads(campaignId);

  const errors = useMemo(
    () => validateStaticStepContent({ channel, action: step?.action, subject, content: body }),
    [channel, step?.action, subject, body],
  );
  const changed = subject.trim() !== initial.subject || body.trim() !== initial.content;
  const showErrors = errors.length > 0 && (changed || wasStatic);

  const handleSave = () => {
    if (errors.length) return;
    saveStep({
      stepId: step.id,
      advanceSettingsData: {
        content_mode: CONTENT_MODES.STATIC,
        template_text: body.trim(),
        ...(hasSubject ? { email_subject: subject.trim() } : {}),
      },
      action: onClose,
      successMessage: wasStatic ? "Fixed message updated." : "Step now sends a fixed message.",
    });
  };

  return (
    <CustomModal isOpen={isOpen} onClose={onClose} className="rounded-lg p-5" width="1080px">
      <div className="flex flex-col gap-4 w-full">
        <ModalHeader title={wasStatic ? "Edit fixed message" : "Use a fixed message"} onClose={onClose} />
        <p className="text-xs text-gray-600 dark:text-gray-300">
          Every lead gets this message with their own details filled in, and no AI runs for this step.
          {!wasStatic &&
            " Leads already in the campaign get it for any send that hasn't happened yet; AI drafts already written for this step are kept but not used."}
        </p>
        <MessageComposer
          channel={channel}
          subject={subject}
          onSubjectChange={setSubject}
          body={body}
          onBodyChange={setBody}
          errors={showErrors ? errors : []}
          previewLeads={leads}
          sender={sender}
        />
        <ModalActions
          onCancel={onClose}
          onConfirm={handleSave}
          confirmText={wasStatic ? "Save" : "Use fixed message"}
          isSaving={isPending}
          disabled={errors.length > 0 || (wasStatic && !changed)}
        />
      </div>
    </CustomModal>
  );
};

/** Switching back to AI has a real cost, so it is confirmed rather than instant. */
export const SwitchToAiModal = ({ isOpen, onClose, step = {} }) => {
  const { mutate: saveStep, isPending } = useUpdateCampaignStepAdvanceSettings();

  const handleConfirm = () =>
    saveStep({
      stepId: step.id,
      advanceSettingsData: { content_mode: CONTENT_MODES.DYNAMIC },
      action: onClose,
      successMessage: "Step is AI-personalized again.",
    });

  return (
    <CustomModal isOpen={isOpen} onClose={onClose} className="rounded-lg p-5" width="440px">
      <div className="flex flex-col gap-4 w-full">
        <ModalHeader title="Switch to AI-personalized?" onClose={onClose} />
        <InlineAlert variant="warning">
          AI will write this step for every lead in the campaign, which uses AI credits. Your fixed
          message is kept, so you can switch back at any time.
        </InlineAlert>
        <ModalActions
          onCancel={onClose}
          onConfirm={handleConfirm}
          confirmText="Switch to AI"
          isSaving={isPending}
        />
      </div>
    </CustomModal>
  );
};
